import React from 'react';
import { LuCheck, LuX, LuMinus, LuPause, LuAlertTriangle } from 'react-icons/lu';
import { APP_HEX } from '@/constant/theme/hex.constant';
import { APP_SEMANTIC } from '@/constant/theme/colors.constant';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StatusBadgeVariant =
    | 'active'    // solid green  — entity is live / enabled
    | 'inactive'  // solid red    — entity is deleted / disabled
    | 'pending'   // solid amber  — awaiting action
    | 'paused'    // solid blue   — temporarily stopped
    | 'warning'   // solid orange — needs attention
    | 'neutral';  // solid gray   — unknown / default

export type StatusBadgeSize = 'xs' | 'sm' | 'md';

export interface StatusBadgeProps {
    /** Visual intent */
    variant?: StatusBadgeVariant;
    /** Circle size */
    size?: StatusBadgeSize;
    /**
     * Tooltip text. Shown via title attribute.
     * Pass a string or leave undefined to suppress.
     */
    tooltip?: string;
    /** data-testid for automation */
    testId?: string;
    /** Extra Tailwind classes on the wrapper */
    className?: string;
}

// ─── Config map ───────────────────────────────────────────────────────────────

const CONFIG: Record<
    StatusBadgeVariant,
    { bg: string; icon: React.ReactElement }
> = {
    active: { bg: APP_HEX.success, icon: <LuCheck strokeWidth={3} /> },
    inactive: { bg: APP_HEX.danger, icon: <LuX strokeWidth={3} /> },
    pending: { bg: APP_HEX.warning, icon: <LuMinus strokeWidth={3} /> },
    paused: { bg: APP_HEX.info, icon: <LuPause strokeWidth={3} /> },
    warning: { bg: APP_HEX.warning, icon: <LuAlertTriangle strokeWidth={2.5} /> },
    neutral: { bg: APP_HEX.muted, icon: <LuMinus strokeWidth={3} /> },
};

const SIZE: Record<StatusBadgeSize, { circle: string; icon: string }> = {
    xs: { circle: 'w-5 h-5', icon: 'w-3 h-3' },
    sm: { circle: 'w-6 h-6', icon: 'w-3.5 h-3.5' },
    md: { circle: 'w-8 h-8', icon: 'w-4 h-4' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export const StatusBadge: React.FC<StatusBadgeProps> = ({
    variant = 'neutral',
    size = 'sm',
    tooltip,
    testId,
    className = '',
}) => {
    const { bg, icon } = CONFIG[variant];
    const { circle, icon: iconSize } = SIZE[size];

    return (
        <span
            data-testid={testId ?? ''}
            title={tooltip}
            className={[
                'inline-flex items-center justify-center rounded-full text-white flex-shrink-0',
                circle,
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            style={{ background: bg }}
        >
            {React.cloneElement(icon, { className: iconSize })}
        </span>
    );
};

export default StatusBadge;
